// src/screens/admin/customerList/CustomerList.jsx

import { useState, useRef, useEffect, useContext } from "react";
import {
  Search,
  Users,
  UserCheck,
  TrendingUp,
  ShoppingBag,
  Mail,
  Phone,
  MapPin,
  Eye,
  ChevronDown,
  Check,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  searchCustomers,
  filterCustomersByStatus,
  calculateCustomerStats,
  formatCurrency,
  getAvatarInitial,
} from "../../../utils/CustomerHelper";
import "../../../assets/css/admin/customers/CustomerList.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";

// ─────────────────────────────────────────────────────────────
// FILTER DROPDOWN
// ─────────────────────────────────────────────────────────────
const FILTER_OPTIONS = ["All Status", "Active", "Inactive", "Blocked"];

const FilterDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="cml-filter-wrapper" ref={wrapperRef}>
      <button
        className="cml-filter-btn"
        onClick={() => setIsOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{value}</span>
        <ChevronDown
          size={16}
          className={`cml-filter-chevron ${isOpen ? "open" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="cml-filter-menu" role="listbox">
          {FILTER_OPTIONS.map((opt) => (
            <div
              key={opt}
              className={`cml-filter-option ${value === opt ? "active" : ""}`}
              role="option"
              aria-selected={value === opt}
              onClick={() => {
                onChange(opt);
                setIsOpen(false);
              }}
            >
              <span>{opt}</span>
              {value === opt && (
                <Check size={14} className="cml-filter-check" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
const CustomerList = () => {
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const { baseUrl, authToken } = useContext(AppContext);

  const viewAllCustomers = () => {
    console.log("view all customers called!");
    axios
      .get(`${baseUrl}/admin/auth/viewAllCustomers`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        console.log(response.data);
        setCustomers(response.data.customers);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    viewAllCustomers();
  }, []);

  // Filter helpers
  const statusMap = {
    "All Status": "all",
    Active: "active",
    Inactive: "inactive",
    Blocked: "blocked",
  };

  const filtered = filterCustomersByStatus(
    searchCustomers(customers, searchTerm),
    statusMap[statusFilter],
  );

  const stats = calculateCustomerStats(customers);

  return (
    <div className="cml-container">
      {/* ── Header ── */}
      <div className="cml-header">
        <div>
          <h1 className="cml-title">Customer Management</h1>
          <p className="cml-subtitle">
            Monitor and manage your furniture store customers
          </p>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="cml-stats-grid">
        <div className="cml-stat-card">
          <div
            className="cml-stat-icon"
            style={{ background: "rgba(214,126,67,0.85)" }}
          >
            <Users size={22} color="#fff" />
          </div>
          <div className="cml-stat-info">
            <span className="cml-stat-label">Total Customers</span>
            <span className="cml-stat-value">{stats.total}</span>
          </div>
        </div>
        <div className="cml-stat-card">
          <div
            className="cml-stat-icon"
            style={{ background: "rgba(92,107,71,0.85)" }}
          >
            <UserCheck size={22} color="#fff" />
          </div>
          <div className="cml-stat-info">
            <span className="cml-stat-label">Active Customers</span>
            <span className="cml-stat-value">{stats.active}</span>
          </div>
        </div>
        <div className="cml-stat-card">
          <div
            className="cml-stat-icon"
            style={{ background: "rgba(214,126,67,0.85)" }}
          >
            <ShoppingBag size={22} color="#fff" />
          </div>
          <div className="cml-stat-info">
            <span className="cml-stat-label">Total Orders</span>
            <span className="cml-stat-value">{stats.totalOrders}</span>
          </div>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="cml-controls">
        <div className="cml-search">
          <Search size={15} className="cml-search-icon" />
          <input
            type="text"
            className="cml-search-input"
            placeholder="Search by name, email or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
          />
        </div>
        <FilterDropdown value={statusFilter} onChange={setStatusFilter} />
      </div>

      {/* ── Table ── */}
      <div className="cml-table-container">
        {filtered.length === 0 ? (
          <div className="cml-empty">
            <span className="cml-empty-icon">👥</span>
            <h3>No customers found</h3>
            <p>Try adjusting your search or filter</p>
          </div>
        ) : (
          <table className="cml-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Contact</th>
                <th>Location</th>
                <th>Orders</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id}>
                  {/* Customer */}
                  <td>
                    <div className="cml-customer">
                      <div
                        className="cml-avatar"
                        style={{ background: c.avatarColor || "#D67E43" }}
                      >
                        {c.fullName
                          ?.split(" ")
                          .map((n) => n[0])
                          .slice(0, 2)
                          .join("")}
                      </div>
                      <span className="cml-customer-name">{c.name}</span>
                    </div>
                  </td>

                  {/* Contact */}
                  <td>
                    <div className="cml-contact-col">
                      <span className="cml-contact-row">
                        <Mail size={13} className="cml-contact-icon" />
                        {c.email}
                      </span>
                      <span className="cml-contact-row">
                        <Phone size={13} className="cml-contact-icon" />
                        {c.phone}
                      </span>
                    </div>
                  </td>

                  {/* Location */}
                  <td>
                    <span className="cml-location">
                      <MapPin size={13} className="cml-contact-icon" />
                      {c.address}
                    </span>
                  </td>

                  {/* Orders */}
                  <td className="cml-orders">{c.orders_count}</td>

                  {/* Status */}
                  <td>
                    <span className={`cml-badge cml-badge-${c.status}`}>
                      {c.status}
                    </span>
                  </td>

                  {/* Actions */}
                  <td>
                    <div className="cml-actions">
                      <button
                        className="cml-action-btn cml-view"
                        title="View"
                        onClick={() => navigate(`/admin/customers/${c.id}`)}
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CustomerList;